import { useEffect, useMemo, useRef } from 'react';
import { UseFieldArrayReturn, UseFormReturn, useWatch } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  PACKAGE_TYPE_TO_CODE,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { DEFAULT_DIMENSION_UNIT } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { DEFAULT_WEIGHT_UNIT } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';

import { LTLTrailerVisualizationBlock } from '../LTLTrailerVisualizationBlock';
import {
  buildItemColorMap,
  useStableItemIds,
} from '../components/visualizationColors';
import { useIs3DVisualisationEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';
import { ItemCard } from './ItemCard';
import { TURVO_QTY_UNIT_DEFAULT } from './constants';

interface ItemsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  itemFieldArray: Pick<
    UseFieldArrayReturn<QuickQuoteInputs, 'ltl.items'>,
    'fields' | 'append' | 'remove' | 'replace'
  >;
}

// LTL items section: name, dimensions, weight, qty, freight class per item.
export function ItemsSection({
  formMethods,
  itemFieldArray,
}: ItemsSectionProps) {
  const {
    control,
    formState: { errors },
  } = formMethods;

  const { fields, append, remove, replace } = itemFieldArray;
  const items = useWatch({ control, name: 'ltl.items' }) ?? [];
  const is3DVisualisationEnabled = useIs3DVisualisationEnabled();
  const itemIds = useStableItemIds(items);
  const itemColorMap = useMemo(() => buildItemColorMap(itemIds), [itemIds]);

  const hasInitializedRef = useRef(false);

  const itemsError = errors.ltl?.items;
  const arrayLevelError =
    itemsError && 'root' in itemsError && itemsError.root?.message
      ? itemsError.root.message
      : itemsError && 'message' in itemsError
        ? (itemsError.message as string)
        : null;

  const createEmptyItem = () => ({
    itemId: crypto.randomUUID(),
    description: '',
    packageType: PACKAGE_TYPE_TO_CODE['Pallet'],
    handlingUnitType: TURVO_QTY_UNIT_DEFAULT,
    dimensionUnit: DEFAULT_DIMENSION_UNIT,
    weightUnit: DEFAULT_WEIGHT_UNIT,
  });

  useEffect(() => {
    if (fields.length === 0) {
      hasInitializedRef.current = false;
    }
  }, [fields.length]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    if (fields.length === 0) {
      append(createEmptyItem());
    }
  }, []);

  const handleRemoveItem = (index: number) => {
    remove(index);
  };

  const handleAddItem = () => {
    const item = createEmptyItem();

    if (fields.length === 0) {
      replace([item]);
      return;
    }
    append(item);
  };

  return (
    <Flex direction='col' gap='lg' className='w-full mx-0'>
      <Flex align='center' justify='between' className='w-full mx-0'>
        <Typography
          variant='h5'
          className='text-neutral-800 font-semibold whitespace-nowrap'
        >
          Items <span className='text-error-500'>*</span>
        </Typography>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleAddItem}
          className='h-8 px-3 text-xs'
          buttonNamePosthog={null}
        >
          <Plus className='h-4 w-4 mr-1' />
          Add Item
        </Button>
      </Flex>

      {fields.length === 0 && (
        <Flex
          direction='col'
          align='center'
          justify='center'
          className={`text-center py-6 text-sm border border-dashed rounded-md w-full mx-0 ${
            arrayLevelError
              ? 'border-error-500 bg-error-50 text-error-600'
              : 'border-neutral-300 bg-neutral-50/50 text-neutral-500'
          }`}
        >
          {arrayLevelError || 'No items added yet.'}
        </Flex>
      )}

      <AnimatePresence>
        <Flex
          direction='col'
          gap={fields.length > 0 ? 'md' : 'none'}
          className='w-full mx-0'
        >
          {fields.map((field, index) => (
            <ItemCard
              key={field.id}
              index={index}
              itemId={field.itemId}
              formMethods={formMethods}
              itemColorMap={itemColorMap}
              onRemove={() => handleRemoveItem(index)}
            />
          ))}
        </Flex>
      </AnimatePresence>

      {is3DVisualisationEnabled && (
        <LTLTrailerVisualizationBlock formMethods={formMethods} />
      )}
    </Flex>
  );
}
